import React, { FC, useState } from 'react';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { GenericTreeItem } from './CommonTreeView';
import { ReactComponent as ArrowDownIcon } from '../../images/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../../images/arrow-up.svg';
import { ReactComponent as FolderIcon } from '../../images/folder.svg';
import { ReactComponent as FolderOpenIcon } from '../../images/folder-open.svg';
import { COLORS } from '../../constants/constants';

interface CommonTreeViewItemProps {
    node: GenericTreeItem;
    activeId: number | string;
    onItemClickHandler: (item: GenericTreeItem) => void;
    indentation: number;
    unfoldTree?: boolean;
    nodeInitialOpenDropdown?: (node: GenericTreeItem) => boolean;
}

const CommonTreeViewItem: FC<CommonTreeViewItemProps> = ({
    node,
    activeId,
    onItemClickHandler,
    indentation,
    unfoldTree,
    nodeInitialOpenDropdown,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(nodeInitialOpenDropdown?.(node) || false);

    return (
        <>
            <ListItemButton
                onClick={() => onItemClickHandler(node)}
                selected={activeId === node.id}
                sx={{
                    'height': '32px',
                    'pl': `${indentation}px`,
                    'paddingRight': '5px',
                    '&.Mui-selected': {
                        borderLeft: `4px solid transparent !important`,
                        backgroundColor: `${COLORS.BRAND_LIGHTEST} !important`,
                    },
                    '&:hover': {
                        backgroundColor: `${COLORS.BRAND_LIGHTEST} !important`,
                    },
                }}
            >
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: '11px' }}>
                        {isDropdownOpen ? <FolderOpenIcon className="fill-md" /> : <FolderIcon className="fill-md" />}
                    </Box>
                    <ListItemText
                        sx={{
                            '& .MuiTypography-root': {
                                textWrap: 'nowrap',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                        }}
                    >
                        <Typography title={node.name}>{node.name}</Typography>
                    </ListItemText>
                    {node.children.length > 0 && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsDropdownOpen((prev) => !prev);
                            }}
                        >
                            {unfoldTree || isDropdownOpen ? (
                                <ArrowUpIcon className="fill-md" />
                            ) : (
                                <ArrowDownIcon className="fill-md" />
                            )}
                        </IconButton>
                    )}
                </Box>
            </ListItemButton>
            {node.children.length > 0 && (
                <Collapse in={unfoldTree || isDropdownOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {node.children.map((childNode) => (
                            <CommonTreeViewItem
                                node={childNode}
                                key={childNode.id}
                                activeId={activeId}
                                onItemClickHandler={onItemClickHandler}
                                indentation={indentation + 15}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

export default React.memo(CommonTreeViewItem);
